"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createSkillsEditDialog = createSkillsEditDialog;
var _dialog = require("../../ui/dialog");
var _litHtml = require("lit-html");
require("../../styles/EditDialogs.css");
require("../../styles/ContactInfoEditDialog.css");
var _rowState = require("../shared/rowState");
var _textUtils = require("../../textUtils");
var _mutations = require("./mutations");
var _profileIcons = require("../../icons-svg/profileIcons");
var _texts = require("../../texts");
const ESCO_SKILL_SEARCH_URI = 'https://ec.europa.eu/esco/api/search?language=$(language)&limit=$(limit)&type=skill&text=$(name)';
const ESCO_SEARCH_LANGUAGE = 'en';
const ESCO_SEARCH_LIMIT = 8;
const ESCO_SKILL_BASE_URI = 'http://data.europa.eu/esco/skill/';
function normalizeSkillPublicId(value) {
  const normalized = sanitizeSkillFieldValue(value);
  if (!normalized) return '';
  if (normalized.startsWith('skill:')) return normalized;
  if (normalized.startsWith(ESCO_SKILL_BASE_URI)) {
    const suffix = normalized.slice(ESCO_SKILL_BASE_URI.length);
    return suffix ? `skill:${suffix}` : normalized;
  }
  return normalized;
}
function buildEscoSkillSearchUrl(name) {
  return ESCO_SKILL_SEARCH_URI.replace('$(language)', encodeURIComponent(ESCO_SEARCH_LANGUAGE)).replace('$(limit)', encodeURIComponent(String(ESCO_SEARCH_LIMIT))).replace('$(name)', encodeURIComponent(name));
}
function toSkillLabel(result) {
  return result?.title || result?.searchHit || result?.preferredLabel?.en || result?.uri || '';
}
async function fetchEscoSkillSuggestions(name) {
  const query = sanitizeSkillFieldValue(name);
  if (query.length < 2 || typeof fetch !== 'function') return [];
  try {
    const response = await fetch(buildEscoSkillSearchUrl(query), {
      headers: {
        Accept: 'application/json'
      }
    });
    if (!response.ok) return [];
    const payload = await response.json();
    const results = Array.isArray(payload?._embedded?.results) ? payload._embedded.results : [];
    const seen = new Set();
    return results.map(result => {
      const label = sanitizeSkillFieldValue(toSkillLabel(result));
      const uri = typeof result?.uri === 'string' ? result.uri : '';
      return {
        label,
        uri
      };
    }).filter(suggestion => {
      if (!suggestion.label) return false;
      const key = suggestion.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch {
    return [];
  }
}
function sanitizeSkillFieldValue(value) {
  return (0, _textUtils.sanitizeTextValue)(value);
}
function rowHasContent(row) {
  return [row.name].some(_textUtils.hasNonEmptyText);
}
function toFormState(details) {
  const rows = (details || []).map(detail => ({
    name: sanitizeSkillFieldValue((0, _textUtils.toText)(detail.name)),
    publicId: normalizeSkillPublicId((0, _textUtils.toText)(detail.publicId)),
    entryNode: (0, _textUtils.toText)(detail.entryNode),
    status: (0, _textUtils.toText)(detail.entryNode) ? 'existing' : 'new'
  })).filter(row => Boolean(row.name || row.publicId || row.entryNode));
  return {
    skills: rows.length ? rows : [{
      name: '',
      publicId: '',
      entryNode: '',
      status: 'new'
    }]
  };
}
function matchSkillSuggestion(suggestions, value) {
  const normalized = sanitizeSkillFieldValue(value).toLowerCase();
  if (!normalized) return null;
  return suggestions.find(suggestion => suggestion.label.toLowerCase() === normalized) || null;
}
function validateSkillsBeforeSave(rows) {
  const ops = (0, _rowState.summarizeRowOps)(rows, rowHasContent);
  const hasChanges = ops.create.length > 0 || ops.update.length > 0 || ops.remove.length > 0;
  if (!hasChanges) return 'No skill changes detected.';
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.status === 'deleted') continue;
    if (!(0, _textUtils.hasNonEmptyText)(row.name)) continue;
    if (row.status !== 'existing' && !(0, _textUtils.hasNonEmptyText)(row.publicId)) {
      return `Skill ${i + 1}: please select a skill from the ESCO suggestions.`;
    }
  }
  return null;
}
function hasInvalidSkillSelection(rows) {
  return rows.some(row => {
    if (!row || row.status === 'deleted') return false;
    if (!(0, _textUtils.hasNonEmptyText)(row.name)) return false;
    return !(0, _textUtils.hasNonEmptyText)(row.publicId);
  });
}
function updateSkillsSubmitEnabled(rows) {
  const submitButton = document.querySelector('#profile-modal #modal-buttons button.btn-primary');
  if (!submitButton) return;
  submitButton.disabled = hasInvalidSkillSelection(rows);
}
function focusSkillField(form, selector) {
  const nextField = form.querySelector(selector);
  if (!nextField || typeof nextField.focus !== 'function') return;
  nextField.scrollIntoView({
    block: 'start',
    behavior: 'auto'
  });
  nextField.focus();
  if (nextField instanceof HTMLInputElement || nextField instanceof HTMLTextAreaElement) {
    nextField.select();
  }
}
function renderSkillInputRow({
  rows,
  index,
  displayIndex,
  onDelete,
  onChange,
  onSkillSearch,
  suggestions
}) {
  const row = rows[index];
  const label = `Skill ${displayIndex + 1}`;
  const skillName = `skill-${index}`;
  const datalistId = `skill-suggestions-${index}`;
  const hasSelectionIssue = Boolean(row && row.status !== 'deleted' && (0, _textUtils.hasNonEmptyText)(row.name) && !(0, _textUtils.hasNonEmptyText)(row.publicId));
  const handleSkillInput = field => e => {
    const target = e.target;
    const nextValue = sanitizeSkillFieldValue(target.value);
    if (rows[index]) {
      (0, _rowState.applyRowFieldChange)(rows[index], field, nextValue, rowHasContent);
      const matchedSuggestion = matchSkillSuggestion(suggestions, nextValue);
      rows[index].publicId = normalizeSkillPublicId(matchedSuggestion?.uri || '');
      onSkillSearch(index, nextValue);
      onChange();
    }
  };
  const handleDelete = event => {
    event.preventDefault();
    onDelete();
  };
  return (0, _litHtml.html)`
    <div class="profile-edit-dialog__row profile-edit-dialog__row--full profile-edit-dialog__row--skill">
      <label aria-label=${`${label} Name`} class="label profile-edit-dialog__field profile-edit-dialog__field--full">
        <div class="profile-edit-dialog__input-wrap">
          <span class="profile-edit-dialog__search-icon" aria-hidden="true">${_profileIcons.searchIcon}</span>
          <input
            class="input profile-edit-dialog__input--with-leading-icon"
            type="text"
            name=${skillName}
            .value=${row?.name || ''}
            required
            data-contact-field="name"
            data-entry-node=${row?.entryNode || ''}
            data-row-status=${row?.status || 'n/a'}
            placeholder="Skill"
            autocomplete="off"
            list=${datalistId}
            inputmode="text"
            aria-invalid=${hasSelectionIssue ? 'true' : 'false'}
            @input=${handleSkillInput('name')}
          />
        </div>
        <datalist id=${datalistId}>
          ${suggestions.map(suggestion => (0, _litHtml.html)`<option value=${suggestion.label}></option>`)}
        </datalist>
        <small class="profile-edit-dialog__input-help-text">Type to search ESCO and select one suggestion.</small>
      </label>
      <div class="profile-edit-dialog__actions profile-edit-dialog__actions--edge">
        <button
          type="button"
          class="profile-edit-dialog__delete-button"
          aria-label=${`Delete skill ${displayIndex + 1}`}
          title=${_texts.deleteEntryButtonTitleText}
          @click=${handleDelete}
        >
          <span class="profile-edit-dialog__delete-icon" aria-hidden="true">${_profileIcons.trashIcon}</span>
        </button>
      </div>
    </div>
  `;
}
function renderSkillsSection(rows, onAddRow, suggestionByIndex, handleSearch) {
  const visibleRows = rows.map((row, index) => ({
    row,
    index
  })).filter(({
    row
  }) => row.status !== 'deleted');
  return (0, _litHtml.html)`
    <section class="profile-edit-dialog__section" aria-label="Skills">
      <fieldset>
        <legend class="sr-only">Skill entries</legend>
        ${visibleRows.map(({
    index
  }, displayIndex) => renderSkillInputRow({
    rows,
    index,
    displayIndex,
    onChange: onAddRow,
    onSkillSearch: handleSearch,
    suggestions: suggestionByIndex[index] || [],
    onDelete: () => {
      (0, _rowState.deleteRow)(rows, index);
      delete suggestionByIndex[index];
      onAddRow();
    }
  }))}
      </fieldset>
    </section>
  `;
}
function renderSkillsEditTemplate(form, formState, viewerMode, options = {}) {
  const formStateWithSearch = formState;
  const suggestionByIndex = formStateWithSearch.suggestionByIndex || (formStateWithSearch.suggestionByIndex = {});
  const searchSeqByIndex = formStateWithSearch.searchSeqByIndex || (formStateWithSearch.searchSeqByIndex = {});
  const searchTimerByIndex = formStateWithSearch.searchTimerByIndex || (formStateWithSearch.searchTimerByIndex = {});
  const rerender = (nextOptions = {}) => renderSkillsEditTemplate(form, formState, viewerMode, nextOptions);
  const handleSearch = (rowIndex, term) => {
    if (searchTimerByIndex[rowIndex]) {
      clearTimeout(searchTimerByIndex[rowIndex]);
    }
    const normalized = sanitizeSkillFieldValue(term);
    if (normalized.length < 2) {
      suggestionByIndex[rowIndex] = [];
      rerender();
      return;
    }
    const seq = (searchSeqByIndex[rowIndex] || 0) + 1;
    searchSeqByIndex[rowIndex] = seq;
    searchTimerByIndex[rowIndex] = setTimeout(async () => {
      const suggestions = await fetchEscoSkillSuggestions(normalized);
      if (searchSeqByIndex[rowIndex] !== seq) return;
      suggestionByIndex[rowIndex] = suggestions;
      const row = formState.skills[rowIndex];
      if (row) {
        const matchedSuggestion = matchSkillSuggestion(suggestions, row.name);
        row.publicId = matchedSuggestion ? normalizeSkillPublicId(matchedSuggestion.uri) : row.publicId;
      }
      rerender();
    }, 220);
  };
  (0, _litHtml.render)((0, _litHtml.html)`
    ${renderSkillsSection(formState.skills, rerender, suggestionByIndex, handleSearch)}
    ${viewerMode !== 'owner' ? (0, _litHtml.html)`<p class="profile-edit-dialog__login-message">${_texts.ownerLoginRequiredDialogMessageText}</p>` : null}
  `, form);
  updateSkillsSubmitEnabled(formState.skills);
  if (options.focusSelector) {
    focusSkillField(form, options.focusSelector);
  }
}
function createSkillsEditForm(details, viewerMode) {
  const form = document.createElement('form');
  form.classList.add('profile__edit-form');
  const formState = toFormState(details);
  renderSkillsEditTemplate(form, formState, viewerMode);
  const addRow = () => {
    formState.skills.unshift({
      name: '',
      publicId: '',
      entryNode: '',
      status: 'new'
    });
    const formStateWithSearch = formState;
    formStateWithSearch.suggestionByIndex = {};
    formStateWithSearch.searchSeqByIndex = {};
    formStateWithSearch.searchTimerByIndex = {};
    renderSkillsEditTemplate(form, formState, viewerMode, {
      focusSelector: '[name="skill-0"]'
    });
  };
  return {
    form,
    formState,
    addRow
  };
}
async function createSkillsEditDialog(event, store, subject, skills, viewerMode, onSaved) {
  const dom = event.currentTarget?.ownerDocument || document;
  const {
    form,
    formState,
    addRow
  } = createSkillsEditForm(skills, viewerMode);
  const result = await (0, _dialog.openInputDialog)({
    title: _texts.editSkillsDialogTitleText,
    dom,
    form,
    headerAction: {
      type: 'button',
      label: '+ Add More',
      ariaLabel: 'Add another skill',
      onClick: addRow
    },
    submitLabel: _texts.dialogSubmitLabelText,
    cancelLabel: _texts.dialogCancelLabelText,
    validate: () => {
      if (viewerMode !== 'owner') {
        return _texts.ownerLoginRequiredDialogMessageText;
      }
      return validateSkillsBeforeSave(formState.skills);
    },
    onSave: async () => {
      const skillOps = (0, _rowState.summarizeRowOps)(formState.skills, rowHasContent);
      const plan = {
        create: skillOps.create,
        update: skillOps.update,
        remove: skillOps.remove
      };
      await (0, _mutations.processSkillsMutations)(store, subject, plan);
    },
    formatSaveError: error => {
      const message = error instanceof Error ? error.message : String(error);
      return `${_texts.saveContactUpdatesFailedPrefixText} ${message}`;
    }
  });
  if (!result) return;
  if (onSaved) {
    await onSaved();
  }
}